import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { ExamInstanceData, IExamInstanceData } from '../models/exam-instance-data.model';
import { IAnswer } from '../models/answer.model';
import { ISubmitResult, SubmitResult } from '../models/submit-result.model';
import { IPresign, Presign } from '../models/presign.model';

@Injectable({
  providedIn: 'root'
})
export class ExamService {

  public lastSubmitResult?: SubmitResult;

  constructor(
    private http: HttpClient
  ) { }

  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║
   ╚═════╝ ╚══════╝   ╚═╝

  */
  // region getInstance
  async getInstance(
    idOrFolio: string
  ): Promise<ExamInstanceData | undefined> {

    let instanceData: ExamInstanceData | undefined = undefined;

    const urlApi = `${environment.urlApi}/exams/${idOrFolio}`;

    try {

      const response = await new Promise<ServerResponse<IExamInstanceData>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<IExamInstanceData>>(
            urlApi,
            {
              headers: {
                "content-type": "application/json",
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<IExamInstanceData>
              ) => resolve(
                new ServerResponse<IExamInstanceData>(response)
              ),
            error: (error) => {
              if(error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {
        instanceData = new ExamInstanceData(response.data!);
      }

    } catch(error) {
      console.log(error);
    }

    return instanceData;

  }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  // region saveAnswer
  async saveAnswer(
    idOrFolio: string,
    answer: IAnswer
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/exams/${idOrFolio}/answers`;

    try {

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify(answer),
            {
              headers: {
                "content-type": "application/json",
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse
              ) => resolve(
                new ServerResponse(response)
              ),
            error: (error) => {
              if(error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200 || response.statusCode === 201) {
        flag = true;
      }

    } catch(error) {
      console.log(error);
    }

    return flag;

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  // region submit
  async submit(
    idOrFolio: string
  ): Promise<SubmitResult | undefined> {

    let result: SubmitResult | undefined = undefined;

    const urlApi = `${environment.urlApi}/exams/${idOrFolio}/submit`;

    try {

      const response = await new Promise<ServerResponse<{ result: ISubmitResult }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ result: ISubmitResult }>>(
            urlApi,
            JSON.stringify({}),
            {
              headers: {
                "content-type": "application/json",
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<{ result: ISubmitResult }>
              ) => resolve(
                new ServerResponse<{ result: ISubmitResult }>(response)
              ),
            error: (error) => {
              if(error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {
        result = new SubmitResult(response.data!.result);
        this.lastSubmitResult = result;
      }

    } catch(error) {
      console.log(error);
    }

    return result;

  }

  /*
  ██████╗ ██████╗ ███████╗███████╗██╗ ██████╗ ███╗   ██╗
  ██╔══██╗██╔══██╗██╔════╝██╔════╝██║██╔════╝ ████╗  ██║
  ██████╔╝██████╔╝█████╗  ███████╗██║██║  ███╗██╔██╗ ██║
  ██╔═══╝ ██╔══██╗██╔══╝  ╚════██║██║██║   ██║██║╚██╗██║
  ██║     ██║  ██║███████╗███████║██║╚██████╔╝██║ ╚████║
  ╚═╝     ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝ ╚═════╝╚═╝  ╚═══╝

  */
  // region createSignaturePresign
  async createSignaturePresign(
    idOrFolio: string
  ): Promise<Presign | undefined> {

    let presign: Presign | undefined = undefined;

    const urlApi = `${environment.urlApi}/certificates/${idOrFolio}/signature`;

    try {

      const response = await new Promise<ServerResponse<{ presign: IPresign }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ presign: IPresign }>>(
            urlApi,
            JSON.stringify({ contentType: 'image/png' }),
            {
              headers: {
                'content-type': 'application/json',
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<{ presign: IPresign }>
              ) => resolve(
                new ServerResponse<{ presign: IPresign }>(response)
              ),
            error: (error) => {
              if(error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 201) {
        presign = new Presign(response.data!.presign);
      }

    } catch(error) {
      console.log(error);
    }

    return presign;

  }

}
